
import { useState, useRef, useEffect } from 'react';

interface FirewallFeature {
  title: string;
  description: string;
  icon: string;
  stats: string;
}

export default function FirewallSecurity() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const features: FirewallFeature[] = [
    {
      title: "Next-Gen Firewall",
      description: "Advanced application-layer filtering and deep packet inspection",
      icon: "ri-shield-flash-line",
      stats: "99.9% Threat Detection"
    },
    {
      title: "Network Segmentation",
      description: "Micro-segmentation to isolate and protect critical network zones",
      icon: "ri-route-line",
      stats: "Zero Lateral Movement"
    },
    {
      title: "Intrusion Prevention",
      description: "Real-time blocking of malicious network traffic and attacks",
      icon: "ri-alarm-warning-line",
      stats: "< 1ms Response Time"
    },
    {
      title: "VPN Security",
      description: "Secure remote access with encrypted tunneling protocols",
      icon: "ri-globe-line",
      stats: "Military-Grade Encryption"
    },
    {
      title: "DDoS Protection",
      description: "Automated mitigation of distributed denial-of-service attacks",
      icon: "ri-shield-line",
      stats: "1Tbps+ Mitigation"
    },
    {
      title: "Traffic Analysis",
      description: "Deep visibility into network traffic patterns and anomalies",
      icon: "ri-line-chart-line",
      stats: "Real-Time Analytics"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = sectionRef.current?.querySelectorAll('.firewall-item');
    items?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Firewall & Network Security
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Advanced firewall technologies and network security solutions to protect your digital perimeter
          </p>
        </div>

        {/* Hero Image with 3D Firewall Visualization */}
        <div className="mb-16 text-center">
          <div className="relative inline-block group">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20network%20firewall%20visualization%20showing%20data%20packets%20being%20filtered%20through%20security%20barriers%2C%20glowing%20network%20nodes%2C%20orange%20and%20red%20security%20shields%2C%20digital%20firewall%20architecture%20with%20flowing%20data%20streams%2C%20high-tech%20cybersecurity%20infrastructure%20model&width=1200&height=600&seq=firewall3d&orientation=landscape"
              alt="3D Firewall Visualization"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-700 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 to-red-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              data-index={index}
              className={`firewall-item group relative bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:bg-slate-800/60 transform transition-all duration-700 hover:scale-105 hover:-translate-y-2 cursor-pointer ${
                visibleItems.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredFeature(index)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              {/* Animated gradient border */}
              <div className={`absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300 blur-sm ${
                hoveredFeature === index ? 'animate-pulse' : ''
              }`}></div>
              
              <div className="relative z-10 bg-slate-800/90 rounded-2xl p-8 -m-2">
                {/* 3D Icon with animation */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 mx-auto bg-gradient-to-r from-orange-600 to-red-600 rounded-xl flex items-center justify-center group-hover:animate-pulse transform transition-transform duration-500 ${
                    hoveredFeature === index ? 'rotate-12 scale-110' : ''
                  }`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-20 rounded-xl blur-lg group-hover:blur-xl transition-all duration-300"></div>
                </div>

                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-orange-300 transition-colors duration-300">
                  {feature.title}
                </h3>

                <p className="text-gray-400 mb-6 leading-relaxed">
                  {feature.description}
                </p>

                {/* Stats badge */}
                <div className="bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-400/30 rounded-full px-4 py-2 text-center">
                  <span className="text-sm font-semibold text-orange-300">{feature.stats}</span>
                </div>

                {/* Floating particle effect */}
                {hoveredFeature === index && (
                  <div className="absolute top-4 right-4 w-3 h-3 bg-orange-400 rounded-full animate-ping"></div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Network Architecture Visualization */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                Advanced Network Protection
              </span>
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:border-orange-400/50 transition-colors duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white">Multi-Layer Defense</h4>
                  <p className="text-gray-400 text-sm">Comprehensive protection across all network layers</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:border-orange-400/50 transition-colors duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-eye-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white">Deep Visibility</h4>
                  <p className="text-gray-400 text-sm">Complete network traffic analysis and monitoring</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:border-orange-400/50 transition-colors duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-flashlight-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white">Automated Response</h4>
                  <p className="text-gray-400 text-sm">Instant threat mitigation and blocking</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <img 
              src="https://readdy.ai/api/search-image?query=Professional%20network%20security%20diagram%20showing%20firewall%20architecture%2C%20multiple%20security%20layers%2C%20data%20flow%20visualization%2C%20network%20topology%20with%20security%20zones%2C%20enterprise-grade%20firewall%20infrastructure%20with%20monitoring%20dashboards%20and%20security%20metrics&width=700&height=500&seq=networkarch&orientation=landscape"
              alt="Network Architecture"
              className="w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-transform duration-700"
            />
            
            {/* Floating 3D security elements */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center animate-bounce shadow-lg">
              <i className="ri-shield-flash-line text-white text-xl"></i>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
